import React, { useEffect, useState, useCallback } from 'react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area, ComposedChart, RadialBarChart, RadialBar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { Package, DollarSign, Users, Clock, Loader, CheckCircle, Calendar, CreditCard, ShoppingBag, XCircle, AlertTriangle, Sparkles } from 'lucide-react';
import { adminAPI } from '../../services/api';
import './Dashboard.css';

// ── Helpers ────────────────────────────────────────────────────────────────

function toLocalDate(d: Date) {
  return d.toISOString().slice(0, 10);
}

function formatDateLabel(iso: string) {
  const [, m, day] = iso.split('-');
  return `${day}/${m}`;
}

const STATUS_COLORS: Record<string, string> = {
  delivered:   '#10b981',
  partial_delivered: '#f59e0b',
  processing:  '#3b82f6',
  shipped:     '#6366f1',
  pending:     '#f97316',
  no_response: '#f59e0b',
  preorder:    '#8b5cf6',
  cancelled:   '#ef4444',
};
const SOURCE_COLORS: Record<string, string> = {
  Website: '#3b82f6',
  Manual: '#8b5cf6',
};
const PIE_FALLBACK = ['#3b82f6','#10b981','#f97316','#8b5cf6','#ef4444','#f59e0b','#6366f1','#ec4899', '#14b8a6', '#06b6d4', '#84cc16'];

// ── Custom Tooltips ────────────────────────────────────────────────────────

const ChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  
  const displayLabel = label || payload[0]?.name;

  return (
    <div className="chart-tooltip">
      {displayLabel && <p className="tt-label">{displayLabel}</p>}
      {payload.map((entry: any, index: number) => {
        const formatter = entry.formatter;
        let val = entry.value;
        let name = entry.name;
        
        if (formatter) {
          const formatted = formatter(val, name, entry);
          if (Array.isArray(formatted)) {
            val = formatted[0];
            name = formatted[1];
          } else {
            val = formatted;
          }
        } else {
          val = typeof val === 'number' ? val.toLocaleString() : val;
        }

        const color = entry.color || entry.fill || entry.stroke || '#a78bfa';
        const displayName = name && name !== displayLabel && name !== 'value' ? name : '';

        return (
          <p key={index} className="tt-value" style={{ color }}>
            {val} {displayName}
          </p>
        );
      })}
    </div>
  );
};

// ── Preset helpers ─────────────────────────────────────────────────────────

type Preset = '7d' | '30d' | '90d' | 'custom';

function getPresetDates(p: Preset): { start: string; end: string } {
  const today = new Date();
  const end = toLocalDate(today);
  if (p === '7d')  { const s = new Date(today); s.setDate(s.getDate() - 6);  return { start: toLocalDate(s), end }; }
  if (p === '30d') { const s = new Date(today); s.setDate(s.getDate() - 29); return { start: toLocalDate(s), end }; }
  if (p === '90d') { const s = new Date(today); s.setDate(s.getDate() - 89); return { start: toLocalDate(s), end }; }
  return { start: end, end };
}

// ── Dashboard ──────────────────────────────────────────────────────────────

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<any>(null);
  const [charts, setCharts] = useState<any>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [chartsLoading, setChartsLoading] = useState(true);

  const [preset, setPreset] = useState<Preset>('30d');
  const [startDate, setStartDate] = useState(() => getPresetDates('30d').start);
  const [endDate,   setEndDate]   = useState(() => getPresetDates('30d').end);

  // Stats: auto-refresh every 30 s
  useEffect(() => {
    const load = async (isBackground = false) => {
      try {
        if (!isBackground) setStatsLoading(true);
        setStats(await adminAPI.getDashboardStats());
      } catch (e) { console.error(e); }
      finally { if (!isBackground) setStatsLoading(false); }
    };
    load();
    const t = setInterval(() => load(true), 30000);
    return () => clearInterval(t);
  }, []);

  // Charts: refresh on date change and auto-refresh every 30 s
  const loadCharts = useCallback(async (s: string, e: string, isBackground = false) => {
    try {
      if (!isBackground) setChartsLoading(true);
      setCharts(await adminAPI.getDashboardCharts(s, e));
    } catch (err) { console.error(err); }
    finally { if (!isBackground) setChartsLoading(false); }
  }, []);

  useEffect(() => {
    loadCharts(startDate, endDate);
    const t = setInterval(() => loadCharts(startDate, endDate, true), 30000);
    return () => clearInterval(t);
  }, [startDate, endDate, loadCharts]);

  const handlePreset = (p: Preset) => {
    setPreset(p);
    if (p !== 'custom') {
      const { start, end } = getPresetDates(p);
      setStartDate(start);
      setEndDate(end);
    }
  };

  // Derived data
  const revData      = (charts?.dailyRevenue  ?? []).map((r: any) => ({ ...r, label: formatDateLabel(r.date) }));
  const ordData      = (charts?.dailyOrders   ?? []).map((r: any) => ({ ...r, label: formatDateLabel(r.date) }));
  const statusData   = charts?.ordersByStatus ?? [];
  const topProducts  = charts?.topProducts    ?? [];
  const categoryData = charts?.salesByCategory ?? [];
  const sourceData   = charts?.ordersBySource ?? [];
  const areaDemand = (charts?.areaDemand ?? []).map((c: any, i: number) => ({ ...c, fill: ['#3b82f6', '#10b981'][i % 2] }));

  return (
    <div className="admin-dashboard">
      <section className="charts-hero">
        <div className="date-range-bar">
          <span className="dr-label">Date Range</span>
          <div className="dr-presets">
            {(['7d', '30d', '90d'] as Preset[]).map(p => (
              <button key={p} className={`dr-preset-btn${preset === p ? ' active' : ''}`} onClick={() => handlePreset(p)}>
                {p === '7d' ? '7 Days' : p === '30d' ? '30 Days' : '90 Days'}
              </button>
            ))}
            <button className={`dr-preset-btn${preset === 'custom' ? ' active' : ''}`} onClick={() => handlePreset('custom')}>
              Custom
            </button>
          </div>
          {preset === 'custom' && (
            <div className="dr-custom">
              <input type="date" className="dr-input" value={startDate} max={endDate}
                onChange={e => setStartDate(e.target.value)} />
              <span className="dr-sep">→</span>
              <input type="date" className="dr-input" value={endDate} min={startDate} max={toLocalDate(new Date())}
                onChange={e => setEndDate(e.target.value)} />
            </div>
          )}
          {chartsLoading && <span className="dr-loading">Updating…</span>}
        </div>

        <div className="charts-grid">
          {/* 4. Orders Over Time (Composed) */}
          <div className="chart-card">
            <h4 className="chart-title">Orders Over Time</h4>
            {ordData.length === 0 ? <div className="chart-empty">No data</div> : (
              <ResponsiveContainer minWidth={0} width="100%" height="100%">
                <ComposedChart data={ordData} margin={{ top: 6, right: 0, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#9ca3af' }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 9, fill: '#9ca3af' }} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip content={<ChartTooltip />} formatter={(val: any) => [val, 'orders']} cursor={{fill: '#f9fafb'}} />
                  <Bar dataKey="orders" fill="#e0e7ff" radius={[4, 4, 0, 0]} maxBarSize={20} />
                  <Line type="monotone" dataKey="orders" stroke="#4f46e5" strokeWidth={3} dot={{ r: 4, fill: '#4f46e5', strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 6 }} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* 5. Top Products (Horizontal Bar) */}
          <div className="chart-card">
            <h4 className="chart-title">Top Products</h4>
            {topProducts.length === 0 ? <div className="chart-empty">No data</div> : (
              <ResponsiveContainer minWidth={0} width="100%" height="100%">
                <BarChart layout="vertical" data={topProducts} margin={{ top: 0, right: 15, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} vertical={true} />
                  <XAxis type="number" tick={{ fontSize: 9, fill: '#9ca3af' }} tickLine={false} axisLine={false} allowDecimals={false} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 9, fill: '#374151', fontWeight: 500 }} tickLine={false} axisLine={false} width={115}
                         tickFormatter={(v) => v.length > 16 ? v.slice(0, 16) + '..' : v} />
                  <Tooltip content={<ChartTooltip />} formatter={(val: any) => [val, 'Units Sold']} cursor={{fill: '#f9fafb'}} />
                  <Bar dataKey="sales" fill="#10b981" radius={4} maxBarSize={14} background={{ fill: '#f3f4f6', radius: 4 }} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* 6. Area Product Demand (Donut) */}
          <div className="chart-card">
            <h4 className="chart-title">Area Product Demand</h4>
            {areaDemand.length === 0 ? <div className="chart-empty">No data</div> : (
              <ResponsiveContainer minWidth={0} width="100%" height="100%">
                <PieChart>
                  <Pie data={areaDemand} dataKey="revenue" nameKey="name" cx="40%" cy="50%" innerRadius="50%" outerRadius="80%" paddingAngle={4} cornerRadius={4} stroke="none">
                    {areaDemand.map((entry: any, i: number) => (
                      <Cell key={entry.name} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip />} formatter={(val: any) => [`৳${Number(val).toLocaleString()}`, 'Revenue']} />
                  <Legend layout="vertical" align="right" verticalAlign="middle" iconType="circle" iconSize={8}
                    formatter={(v) => <span style={{ fontSize: 10, color: '#374151' }}>{v}</span>} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* 7. Orders by Status (Bar) */}
          <div className="chart-card">
            <h4 className="chart-title">Orders by Status</h4>
            {statusData.length === 0 ? <div className="chart-empty">No data</div> : (
              <ResponsiveContainer minWidth={0} width="100%" height="100%">
                <BarChart data={statusData} margin={{ top: 6, right: 0, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#9ca3af' }} tickLine={false} axisLine={false} interval={0} 
                         tickFormatter={(v) => v.slice(0, 6)} />
                  <YAxis tick={{ fontSize: 9, fill: '#9ca3af' }} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip content={<ChartTooltip />} formatter={(val: any) => [val, 'Orders']} cursor={{fill: '#f9fafb'}} />
                  <Bar dataKey="count" radius={[3, 3, 0, 0]} maxBarSize={16}>
                    {statusData.map((entry: any, index: number) => (
                      <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.status] || '#8b5cf6'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* 8. Revenue Trend (Line) */}
          <div className="chart-card">
            <h4 className="chart-title">Revenue Trend</h4>
            {revData.length === 0 ? <div className="chart-empty">No data</div> : (
              <ResponsiveContainer minWidth={0} width="100%" height="100%">
                <LineChart data={revData} margin={{ top: 6, right: 0, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#9ca3af' }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 9, fill: '#9ca3af' }} tickLine={false} axisLine={false}
                    tickFormatter={v => `৳${v >= 1000 ? (v/1000).toFixed(0)+'k' : v}`} />
                  <Tooltip content={<ChartTooltip />} formatter={(val: any) => [`৳${Number(val).toLocaleString()}`, 'Revenue']} />
                  <Line type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={2.5} dot={{ r: 2, fill: '#3b82f6' }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* 9. Financial Overview (Area) */}
          <div className="chart-card">
            <h4 className="chart-title">Revenue vs Cost</h4>
            {revData.length === 0 ? <div className="chart-empty">No data</div> : (
              <ResponsiveContainer minWidth={0} width="100%" height="100%">
                <AreaChart data={revData} margin={{ top: 6, right: 0, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.4}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorCost" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.4}/>
                      <stop offset="95%" stopColor="#f43f5e" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#9ca3af' }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 9, fill: '#9ca3af' }} tickLine={false} axisLine={false}
                    tickFormatter={v => `৳${v >= 1000 ? (v/1000).toFixed(0)+'k' : v}`} />
                  <Tooltip content={<ChartTooltip />} formatter={(val: any, name: any) => [`৳${Number(val).toLocaleString()}`, name === 'revenue' ? 'Revenue' : 'Cost']} />
                  <Area type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} fillOpacity={1} fill="url(#colorRev)" />
                  <Area type="monotone" dataKey="cost" stroke="#f43f5e" strokeWidth={2} fillOpacity={1} fill="url(#colorCost)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>

{/* 1. Sales by Category (Donut Pie) */}
          <div className="chart-card">
            <h4 className="chart-title">Sales by Category</h4>
            {categoryData.length === 0 ? <div className="chart-empty">No data</div> : (
              <ResponsiveContainer minWidth={0} width="100%" height="100%">
                <PieChart>
                  <Pie data={categoryData} dataKey="revenue" nameKey="category" cx="35%" cy="50%" innerRadius="55%" outerRadius="75%" paddingAngle={4} cornerRadius={4} stroke="none">
                    {categoryData.map((entry: any, i: number) => (
                      <Cell key={entry.category} fill={PIE_FALLBACK[i % PIE_FALLBACK.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip />} formatter={(val: any) => [`৳${Number(val).toLocaleString()}`, '']} />
                  <Legend layout="vertical" align="right" verticalAlign="middle" iconType="circle" iconSize={8}
                    formatter={(v) => <span style={{ fontSize: 10, color: '#374151' }}>{v}</span>} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* 8. Quick Stats Overview (HTML) */}
          <div className="chart-card stats-overview-card">
            <h4 className="chart-title">Quick Stats</h4>
            <div className="quick-stats-container">
              <div className="qs-item">
                <div className="qs-icon blue"><Package size={16} /></div>
                <div className="qs-info">
                  <span className="qs-value">{stats?.totalOrders || 0}</span>
                  <span className="qs-label">Total Orders</span>
                </div>
              </div>
              <div className="qs-item">
                <div className="qs-icon green"><DollarSign size={16} /></div>
                <div className="qs-info">
                  <span className="qs-value">৳{stats?.totalRevenue?.toLocaleString() || 0}</span>
                  <span className="qs-label">Total Revenue</span>
                </div>
              </div>
              <div className="qs-item">
                <div className="qs-icon purple"><Users size={16} /></div>
                <div className="qs-info">
                  <span className="qs-value">{stats?.totalCustomers || 0}</span>
                  <span className="qs-label">Total Customers</span>
                </div>
              </div>
            </div>
          </div>

          {/* 9. Actionable KPIs (HTML) */}
          <div className="chart-card kpi-card">
            <h4 className="chart-title">Actionable KPIs</h4>
            <div className="kpi-container">
              <div className="kpi-item pending">
                <div className="kpi-icon"><Clock size={16} /></div>
                <div className="kpi-info">
                  <span className="kpi-label">Pending</span>
                  <span className="kpi-sub">Needs attention</span>
                </div>
                <span className="kpi-value">{stats?.pendingOrders || 0}</span>
              </div>
              <div className="kpi-item processing">
                <div className="kpi-icon"><Loader size={16} /></div>
                <div className="kpi-info">
                  <span className="kpi-label">Processing</span>
                  <span className="kpi-sub">In progress</span>
                </div>
                <span className="kpi-value">{stats?.processingOrders || 0}</span>
              </div>
              <div className="kpi-item delivered">
                <div className="kpi-icon"><CheckCircle size={16} /></div>
                <div className="kpi-info">
                  <span className="kpi-label">Delivered</span>
                  <span className="kpi-sub">Completed</span>
                </div>
                <span className="kpi-value">{stats?.completedOrders || 0}</span>
              </div>
            </div>
          </div>

                  </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          STATS SECTION — visible after scrolling past charts
         ═══════════════════════════════════════════════════════════ */}
      <section className="stats-section">
        <h3 className="section-title">Store Overview</h3>
        {statsLoading ? (
          <div className="loading-spinner">Loading…</div>
        ) : (
          <div className="stats-grid">

            <div className="stat-card">
              <div className="stat-icon orders"><Package size={20} /></div>
              <div className="stat-info"><h3>Total Orders</h3>
                <p className="stat-value">{stats?.totalOrders || 0}</p></div>
            </div>

            <div className="stat-card">
              <div className="stat-icon revenue"><DollarSign size={20} /></div>
              <div className="stat-info"><h3>Total Revenue</h3>
                <p className="stat-value">৳{stats?.totalRevenue?.toLocaleString() || 0}</p></div>
            </div>

            <div className="stat-card">
              <div className="stat-icon customers"><Users size={20} /></div>
              <div className="stat-info"><h3>Total Customers</h3>
                <p className="stat-value">{stats?.totalCustomers || 0}</p></div>
            </div>

            <div className="stat-card warning">
              <div className="stat-icon pending"><Clock size={20} /></div>
              <div className="stat-info"><h3>Pending Orders</h3>
                <p className="stat-value">{stats?.pendingOrders || 0}</p></div>
            </div>

            <div className="stat-card info">
              <div className="stat-icon processing"><Loader size={20} /></div>
              <div className="stat-info"><h3>Processing Orders</h3>
                <p className="stat-value">{stats?.processingOrders || 0}</p></div>
            </div>

            <div className="stat-card success">
              <div className="stat-icon completed"><CheckCircle size={20} /></div>
              <div className="stat-info"><h3>Completed Orders</h3>
                <p className="stat-value">{stats?.completedOrders || 0}</p></div>
            </div>

            <div className="stat-card highlight">
              <div className="stat-icon today"><Calendar size={20} /></div>
              <div className="stat-info"><h3>Today's Orders</h3>
                <p className="stat-value">{stats?.todayOrders || 0}</p></div>
            </div>

            <div className="stat-card highlight">
              <div className="stat-icon today-revenue"><CreditCard size={20} /></div>
              <div className="stat-info"><h3>Today's Revenue</h3>
                <p className="stat-value">৳{stats?.todayRevenue?.toLocaleString() || 0}</p></div>
            </div>

            <div className="stat-card">
              <div className="stat-icon products"><ShoppingBag size={20} /></div>
              <div className="stat-info"><h3>Total Products</h3>
                <p className="stat-value">{stats?.totalProducts || 0}</p></div>
            </div>

            <div className="stat-card danger">
              <div className="stat-icon out-stock"><XCircle size={20} /></div>
              <div className="stat-info"><h3>Out of Stock</h3>
                <p className="stat-value">{stats?.outOfStock || 0}</p></div>
            </div>

            <div className="stat-card warning">
              <div className="stat-icon low-stock"><AlertTriangle size={20} /></div>
              <div className="stat-info"><h3>Low Stock (≤10)</h3>
                <p className="stat-value">{stats?.lowStock || 0}</p></div>
            </div>

            <div className="stat-card success">
              <div className="stat-icon active-products"><Sparkles size={20} /></div>
              <div className="stat-info"><h3>Active Products</h3>
                <p className="stat-value">{stats?.activeProducts || 0}</p></div>
            </div>

          </div>
        )}
      </section>

      {/* Recent Orders */}
      <div className="dashboard-section">
        <h3 className="section-title">Recent Orders</h3>
        <div className="recent-orders-table">
          <table>
            <thead>
              <tr>
                <th>Order ID</th><th>Customer</th><th>Date</th><th>Status</th><th>Total</th>
              </tr>
            </thead>
            <tbody>
              {stats?.recentOrders?.map((order: any) => (
                <tr key={order.id}>
                  <td>#{order.id}</td>
                  <td>{order.customer}</td>
                  <td>{order.date}</td>
                  <td>
                    <span className={`status-badge ${order.status.toLowerCase().replace(/\s+/g, '-')}`}>
                      {order.status}
                    </span>
                  </td>
                  <td>৳{order.total.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};

export default Dashboard;
